import cn from 'classnames';
import parse from 'html-react-parser';
import { FC } from 'react';
import { useSearchParams } from 'next/navigation';
import capitalize from 'lodash-es/capitalize';

import { Button } from '@catawiki/cata-style';

import { cleanBuilderStringInput, getBuilderString } from '@/shared/text';

import { BackgroundClassMap, COLOR_NAME_BRAND } from '../constants/colors';
import { BuilderString } from '../types';
import style from './Action.module.scss';

export enum ActionVariant {
  NONE = 'none',

  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  TERTIARY = 'tertiary',
}

export const getActionVariantName = (actionVariant: ActionVariant) =>
  capitalize(actionVariant).split('_').join(' ');

export interface BaseAction {
  label?: BuilderString;
  url?: BuilderString;
  colorName?: string;
}

export interface Action extends BaseAction {
  locale: string;
  variant?: ActionVariant;
  className?: string;
  colorName?: string;
}

export const Action: FC<Action> = ({
  locale,
  label,
  url,
  className,
  colorName = COLOR_NAME_BRAND,
  variant = ActionVariant.PRIMARY,
}) => {
  const searchParams = useSearchParams();
  if (!label) return null;
  if (variant === ActionVariant.NONE) return null;

  const baseUrl = cleanBuilderStringInput(getBuilderString(url, locale), true);
  const href = searchParams?.toString()
    ? `${baseUrl}?${searchParams.toString()}`
    : baseUrl;

  return (
    <div
      data-testid="action"
      className={cn(style.action, className, {
        [style.collapse]: variant === ActionVariant.TERTIARY,
      })}
    >
      <Button
        className={cn({
          [BackgroundClassMap[colorName]]: ![
            ActionVariant.TERTIARY,
            ActionVariant.SECONDARY,
          ].includes(variant),
        })}
        data-testid="action-button"
        as="a"
        href={href}
        variant={variant}
      >
        {parse(cleanBuilderStringInput(getBuilderString(label, locale), true))}
      </Button>
    </div>
  );
};
